import { useEffect, useState } from 'react';
import ProgressBar from 'components/raffle/ProgressBar';
import { colors } from 'utils/constants';
import RoundedButton from 'components/RoundedButton';
import CircledText from 'components/CircledText';
import $ from 'jquery';

interface INumberPicker {
  minNumber?: number;
  maxNumber: number;
  prepopulateNumbers: number[];
  selectLimit: number;
  colour: string;
  onSelect: (numbers: number[]) => void;
  rowSize?: number;
}
const NumberPicker = ({
  minNumber = 1,
  maxNumber,
  prepopulateNumbers,
  selectLimit,
  colour,
  onSelect,
  rowSize = 7,
}: INumberPicker) => {
  const allNumbers = Array.from(
    new Array(maxNumber - minNumber + 1),
    (x, i) => i + minNumber
  );
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>(
    prepopulateNumbers || []
  );
  const isNumberSelected = (number: number) => selectedNumbers.includes(number);

  return (
    <div className="numberPicker fullWidth flexRow">
      {allNumbers.map((number, i) => (
        <CircledText
          key={i}
          lineColour={isNumberSelected(number) ? 'white' : colour}
          circleColour={colour}
          backgroundColour={isNumberSelected(number) ? colour : 'white'}
          text={number.toString()}
          additionalClassName="circledNumber"
          onClick={() => {
            if (isNumberSelected(number)) {
              // Remove this number from selected numbers array
              const newSelectedNumbers = selectedNumbers.filter(
                (n) => n !== number
              );
              setSelectedNumbers(newSelectedNumbers);
              onSelect(newSelectedNumbers);
            } else if (selectedNumbers.length < selectLimit) {
              // Add this number to selected numbers array
              const newSelectedNumbers = selectedNumbers.concat([number]);
              setSelectedNumbers(newSelectedNumbers);
              onSelect(newSelectedNumbers);
            }
          }}
        />
      ))}

      <style jsx>
        {`
          .numberPicker {
            flex-wrap: wrap;
            flex-shrink: 0;
          }

          :global(.numberPicker .circledText) {
            width: ${100 / rowSize - (2 * 7) / rowSize}%;
            padding-top: ${0.92 * (100 / rowSize - (2 * 7) / rowSize)}%;
            margin: ${7 / rowSize}%;
          }
        `}
      </style>
    </div>
  );
};
interface IWeeklyDrawNumberPicker {
  prepopulateMainNumbers: number[];
  prepopulateBonusNumbers: number[];
  onConfirmNumbers: (main: number[], bonus: number[]) => void;
  numberRanges: number[];
  bonusNumberRanges: number[];
  glowColor?: string;
}
const WeeklyDrawNumberPicker = ({
  prepopulateMainNumbers,
  prepopulateBonusNumbers,
  onConfirmNumbers,
  numberRanges,
  bonusNumberRanges,
  glowColor = '#50E3C2',
}: IWeeklyDrawNumberPicker) => {
  const mainNumberCount = numberRanges.length;
  const bonusNumberCount = bonusNumberRanges.length;

  const [selectedMainNumbers, setSelectedMainNumbers] = useState<number[]>(
    prepopulateMainNumbers || []
  );
  const [selectedBonusNumbers, setSelectedBonusNumbers] = useState<number[]>(
    prepopulateBonusNumbers || []
  );

  const allNumbersHaveBeenSelected =
    selectedMainNumbers.length === mainNumberCount &&
    selectedBonusNumbers.length === bonusNumberCount;

  useEffect(() => {
    if (selectedMainNumbers.length === mainNumberCount) {
      $('#popupScrollBox').animate({
        scrollTop: $('#bonusNumberPicker').offset()?.top,
      });
    }
  }, [selectedMainNumbers.length]);

  return (
    <div className="weeklyDrawNumberPicker fullWidth flexCentered">
      <div className="main numberPickerSection fullWidth flexCentered">
        <div className="headingContainer fullWidth flexRow">
          <p className="heading">Choose {mainNumberCount} main numbers</p>

          <p className="progressCounter">
            {selectedMainNumbers.length} of {mainNumberCount}
          </p>
        </div>

        <ProgressBar
          currentStep={selectedMainNumbers.length}
          totalSteps={mainNumberCount}
        />

        <div className="mainNumberPicker fullWidth flexCentered">
          <NumberPicker
            maxNumber={numberRanges[0]}
            selectLimit={mainNumberCount}
            prepopulateNumbers={selectedMainNumbers}
            colour={colors.primaryBrandGray}
            onSelect={(selectedNumbers) => {
              setSelectedMainNumbers(selectedNumbers);
            }}
          />
        </div>
      </div>

      {bonusNumberCount > 0 && (
        <div
          id="bonusNumberPicker"
          className="bonus numberPickerSection fullWidth flexCentered"
        >
          <div className="headingContainer fullWidth flexRow">
            <p className="heading">
              Choose {bonusNumberCount} bonus number
              {bonusNumberCount > 1 ? 's' : ''}
            </p>

            <p className="progressCounter">
              {selectedBonusNumbers.length} of {bonusNumberCount}
            </p>
          </div>

          <ProgressBar
            currentStep={selectedBonusNumbers.length}
            totalSteps={bonusNumberCount}
          />

          <div className="bonusNumberPicker fullWidth flexCentered">
            <NumberPicker
              maxNumber={bonusNumberRanges[0]}
              selectLimit={bonusNumberCount}
              prepopulateNumbers={selectedBonusNumbers}
              colour={glowColor}
              onSelect={(selectedNumbers) => {
                setSelectedBonusNumbers(selectedNumbers);
              }}
            />
          </div>
        </div>
      )}

      {/*<div*/}
      {/*  onClick={() => {*/}
      {/*    setSelectedMainNumbers([]);*/}
      {/*    setSelectedBonusNumber([]);*/}
      {/*  }}*/}
      {/*  style={{ marginTop: '2em', cursor: 'pointer' }}*/}
      {/*>*/}
      {/*  <p style={{ fontStyle: 'italic', fontWeight: 700 }}>Clear selection</p>*/}
      {/*</div>*/}

      <RoundedButton
        onClick={() =>
          onConfirmNumbers(selectedMainNumbers, selectedBonusNumbers)
        }
        disabled={!allNumbersHaveBeenSelected}
      >
        Confirm
      </RoundedButton>

      <style jsx>
        {`
          .weeklyDrawNumberPicker {
            flex-shrink: 0;
          }

          .weeklyDrawNumberPicker div {
            flex-shrink: 0;
          }

          .numberPickerSection {
            padding: 2em 0;
            flex-shrink: 0;
          }

          .headingContainer {
            justify-content: space-between;
            align-items: center;
            padding: 0 1%;
          }

          .headingContainer p {
            margin: 0;
            font-weight: 700;
            font-size: 1.2em;
            line-height: 1em;
          }

          .headingContainer .progressCounter {
            color: ${colors.primaryBrandGray}90;
          }

          :global(.numberPickerSection .progressBar) {
            margin: 1.5em 0;
            width: 99%;
          }

          :global(.weeklyDrawNumberPickerPopup .roundedButton) {
            margin: 1.5em 0;
            width: 100%;
          }
        `}
      </style>
    </div>
  );
};

export default WeeklyDrawNumberPicker;
